import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useAuth from 'src/hooks/useAuth';
import useAddNotificationEmailMutation from 'src/services/email_notifications/useAddNotificationEmailMutation';
import useDeleteNotificationEmailMutation from 'src/services/email_notifications/useDeleteNotificationEmailMutation';
import useNotificationEmailsQuery from 'src/services/email_notifications/useNotificationEmailsQuery';
import useNotificationTypesQuery from 'src/services/email_notifications/useNotificationTypesQuery';
import useNotificationUserInfoQuery from 'src/services/email_notifications/useNotificationUserInfoQuery';
import useUpdateClientEmailMutation from 'src/services/email_notifications/useUpdateClientEmailMutation';
import useUpdateNotificationEmailMutation from 'src/services/email_notifications/useUpdateNotificationEmailMutation';
import useUpdateNotificationTypesMutation from 'src/services/email_notifications/useUpdateNotificationTypesMutation';
import type { ManageAddressRow, UserInfoData } from 'src/types/email_notifications';
import {
  BUYER_PO_CODES,
  INVOICES_CODES,
  MESSAGES_CODES,
  OCR_CODE,
} from 'src/types/email_notifications/NotificationTypeCodes';
import { createContext, useContext } from 'use-context-selector';

export const NOTIFICATION_METHOD = 'E-Mail';

function mapNotificationEmailsToManageAddresses(
  items: Array<{ contactMethodId: number; emailAddress: string }>
): ManageAddressRow[] {
  return items.map((item, index) => ({
    id: String(item.contactMethodId),
    notificationAddress: item.emailAddress,
    selected: index === 0,
  }));
}

export interface NotificationToggles {
  allNotifications: boolean;
  buyerPurchaseOrders: boolean;
  ocr: boolean;
  buyerInvoices: boolean;
  messages: boolean;
}

const initialToggles: NotificationToggles = {
  allNotifications: false,
  buyerPurchaseOrders: false,
  ocr: false,
  buyerInvoices: false,
  messages: false,
};

/** Which notification type rows to show based on auth token permissions */
export interface NotificationVisibility {
  showPurchaseOrders: boolean;
  showInvoices: boolean;
  showOcr: boolean;
  showMessages: boolean;
}

function mapNotificationCodesToToggles(
  codes: number[],
  visibility?: NotificationVisibility
): NotificationToggles {
  const codeSet = new Set(codes);
  const buyerPurchaseOrders = BUYER_PO_CODES.every((c) => codeSet.has(c));
  const buyerInvoices = INVOICES_CODES.every((c) => codeSet.has(c));
  const ocr = codeSet.has(OCR_CODE);
  const messages = MESSAGES_CODES.every((c) => codeSet.has(c));

  const allNotifications = visibility
    ? (!visibility.showPurchaseOrders || buyerPurchaseOrders) &&
      (!visibility.showInvoices || buyerInvoices) &&
      (!visibility.showOcr || ocr) &&
      (!visibility.showMessages || messages)
    : buyerPurchaseOrders && buyerInvoices && ocr && messages;

  return {
    allNotifications,
    buyerPurchaseOrders,
    ocr,
    buyerInvoices,
    messages,
  };
}

function mapTogglesToTypeCdIds(
  toggles: NotificationToggles,
  visibility?: NotificationVisibility
): {
  selectedTypeCdIds: number[];
  deletedTypeCdIds: number[];
} {
  const selectedTypeCdIds: number[] = [];
  const deletedTypeCdIds: number[] = [];

  if (!visibility || visibility.showPurchaseOrders) {
    if (toggles.buyerPurchaseOrders) selectedTypeCdIds.push(...BUYER_PO_CODES);
    else deletedTypeCdIds.push(...BUYER_PO_CODES);
  }
  if (!visibility || visibility.showInvoices) {
    if (toggles.buyerInvoices) selectedTypeCdIds.push(...INVOICES_CODES);
    else deletedTypeCdIds.push(...INVOICES_CODES);
  }
  if (!visibility || visibility.showOcr) {
    if (toggles.ocr) selectedTypeCdIds.push(OCR_CODE);
    else deletedTypeCdIds.push(OCR_CODE);
  }
  if (!visibility || visibility.showMessages) {
    if (toggles.messages) selectedTypeCdIds.push(...MESSAGES_CODES);
    else deletedTypeCdIds.push(...MESSAGES_CODES);
  }

  return { selectedTypeCdIds, deletedTypeCdIds };
}

function togglesEqual(a: NotificationToggles, b: NotificationToggles): boolean {
  return (
    a.allNotifications === b.allNotifications &&
    a.buyerPurchaseOrders === b.buyerPurchaseOrders &&
    a.buyerInvoices === b.buyerInvoices &&
    a.ocr === b.ocr &&
    a.messages === b.messages
  );
}

export interface ManageEmailsContextType {
  userInfo: UserInfoData;
  displayEmail: string;
  setDisplayEmail: (email: string) => void;
  updateClientEmail: (email: string) => Promise<void>;
  isUpdatingClientEmail: boolean;
  isNotificationUserInfoLoading: boolean;
  notificationUserInfoError: Error | null;
  selectedNotificationAddress: string;
  setSelectedNotificationAddress: (address: string) => void;
  notificationToggles: NotificationToggles;
  notificationVisibility: NotificationVisibility;
  isNotificationTypesLoading: boolean;
  setAllNotifications: (value: boolean) => void;
  setBuyerPurchaseOrders: (value: boolean) => void;
  setOcr: (value: boolean) => void;
  setBuyerInvoices: (value: boolean) => void;
  setMessages: (value: boolean) => void;
  updateNotificationToggles: (newToggles: NotificationToggles) => void;
  saveNotificationToggles: () => Promise<void>;
  hasNotificationTogglesDirty: boolean;
  isUpdatingNotificationTypes: boolean;
  manageAddresses: ManageAddressRow[];
  setManageAddresses: React.Dispatch<React.SetStateAction<ManageAddressRow[]>>;
  isNotificationEmailsLoading: boolean;
  notificationEmailsError: Error | null;
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  editingDraft: string;
  setEditingDraft: (v: string) => void;
  onStartEditManageAddress: (id: string, currentAddress: string) => void;
  onSaveManageAddress: (id: string) => Promise<void>;
  isUpdatingNotificationEmail: boolean;
  onCancelManageAddress: () => void;
  onDeleteManageAddress: (id: string) => Promise<void>;
  isDeletingNotificationEmail: boolean;
  onConfirmAddNotificationEmail: (email: string) => Promise<void>;
  isAddingNotificationEmail: boolean;
  isAddingNewEmail: boolean;
  setIsAddingNewEmail: (value: boolean) => void;
  isUnsavedTogglesDialogOpen: boolean;
  closeUnsavedTogglesDialog: () => void;
  onSaveAndContinueNotificationAddress: () => Promise<void>;
  onDiscardNotificationAddressChanges: () => void;
}

export const ManageEmailsContext = createContext<ManageEmailsContextType>(
  {} as ManageEmailsContextType
);

export type { ManageAddressRow, UserInfoData };

interface ManageEmailsContextProviderProps {
  children: ReactNode;
}

export const ManageEmailsContextProvider: FC<ManageEmailsContextProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const {
    data: notificationUserInfo,
    isLoading: isNotificationUserInfoLoading,
    error: notificationUserInfoError,
  } = useNotificationUserInfoQuery();
  const {
    data: notificationEmailsData,
    isLoading: isNotificationEmailsLoading,
    error: notificationEmailsError,
  } = useNotificationEmailsQuery();
  const updateClientEmailMutation = useUpdateClientEmailMutation();
  const addNotificationEmailMutation = useAddNotificationEmailMutation();
  const updateNotificationEmailMutation = useUpdateNotificationEmailMutation();
  const deleteNotificationEmailMutation = useDeleteNotificationEmailMutation();
  const updateNotificationTypesMutation = useUpdateNotificationTypesMutation();
  const updateClientEmail = useCallback(
    async (email: string) => {
      await updateClientEmailMutation.mutateAsync({ email });
      toast.success(t('emailAddressUpdatedSuccessfully'));
    },
    [updateClientEmailMutation, t]
  );
  const [userInfo, setUserInfo] = useState<UserInfoData>({
    companyName: user?.CompanyName ?? '',
    contactName: user?.ContactName ?? '',
    ssoOrPasswordResetEmail: '',
  });
  const [displayEmail, setDisplayEmail] = useState('');
  const [selectedNotificationAddress, setSelectedNotificationAddress] = useState('');
  const [notificationToggles, setNotificationToggles] =
    useState<NotificationToggles>(initialToggles);
  const [manageAddresses, setManageAddresses] = useState<ManageAddressRow[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [isAddingNewEmail, setIsAddingNewEmail] = useState(false);
  const [isUnsavedTogglesDialogOpen, setIsUnsavedTogglesDialogOpen] = useState(false);
  const [pendingNotificationAddress, setPendingNotificationAddress] = useState<string | null>(null);

  const selectedContactMethodId = useMemo(
    () =>
      manageAddresses.find((r) => r.notificationAddress === selectedNotificationAddress)?.id ??
      null,
    [manageAddresses, selectedNotificationAddress]
  );
  const { data: notificationTypesData, isLoading: isNotificationTypesLoading } =
    useNotificationTypesQuery(selectedContactMethodId);

  const notificationVisibility = useMemo((): NotificationVisibility => {
    const roles = user?.Role ?? [];
    const showPurchaseOrders = roles.includes('CanAccessPos');
    const showInvoices =
      roles.includes('IsBuyer') &&
      (roles.includes('CanReceiveInvoices') || roles.includes('CanRecieveInvoicesCompany'));
    const showOcr = roles.includes('CanAccessInvoiceManager') && roles.includes('UsesOcr');
    const showMessages = true;
    return { showPurchaseOrders, showInvoices, showOcr, showMessages };
  }, [user?.Role]);

  const setAllNotifications = useCallback((value: boolean) => {
    setNotificationToggles((prev) => ({
      ...prev,
      allNotifications: value,
      buyerPurchaseOrders: value,
      ocr: value,
      buyerInvoices: value,
      messages: value,
    }));
  }, []);

  const setBuyerPurchaseOrders = useCallback((value: boolean) => {
    setNotificationToggles((prev) => ({ ...prev, buyerPurchaseOrders: value }));
  }, []);
  const setOcr = useCallback((value: boolean) => {
    setNotificationToggles((prev) => ({ ...prev, ocr: value }));
  }, []);
  const setBuyerInvoices = useCallback((value: boolean) => {
    setNotificationToggles((prev) => ({ ...prev, buyerInvoices: value }));
  }, []);
  const setMessages = useCallback((value: boolean) => {
    setNotificationToggles((prev) => ({ ...prev, messages: value }));
  }, []);

  const updateNotificationToggles = useCallback((newToggles: NotificationToggles) => {
    setNotificationToggles(newToggles);
  }, []);

  const saveNotificationToggles = useCallback(async () => {
    if (!selectedContactMethodId || !selectedNotificationAddress) return;
    const { selectedTypeCdIds, deletedTypeCdIds } = mapTogglesToTypeCdIds(
      notificationToggles,
      notificationVisibility
    );
    await updateNotificationTypesMutation.mutateAsync({
      contactMethodId: Number(selectedContactMethodId),
      electronicAddress: selectedNotificationAddress,
      selectedTypeCdIds,
      deletedTypeCdIds,
    });
    toast.success(t('systemNotificationUpdatedSuccessfully'));
  }, [
    selectedContactMethodId,
    selectedNotificationAddress,
    notificationToggles,
    notificationVisibility,
    updateNotificationTypesMutation,
    t,
  ]);

  const hasNotificationTogglesDirty = useMemo(() => {
    if (!selectedContactMethodId || !notificationTypesData) return false;
    const serverToggles = mapNotificationCodesToToggles(
      notificationTypesData,
      notificationVisibility
    );
    return !togglesEqual(notificationToggles, serverToggles);
  }, [selectedContactMethodId, notificationTypesData, notificationToggles, notificationVisibility]);

  const setSelectedNotificationAddressWithConfirm = useCallback(
    (newAddress: string) => {
      if (newAddress === selectedNotificationAddress) return;
      if (hasNotificationTogglesDirty) {
        setPendingNotificationAddress(newAddress);
        setIsUnsavedTogglesDialogOpen(true);
      } else {
        setSelectedNotificationAddress(newAddress);
      }
    },
    [
      selectedNotificationAddress,
      hasNotificationTogglesDirty,
      setPendingNotificationAddress,
      setIsUnsavedTogglesDialogOpen,
    ]
  );

  const closeUnsavedTogglesDialog = useCallback(() => {
    setIsUnsavedTogglesDialogOpen(false);
    setPendingNotificationAddress(null);
  }, []);

  const onSaveAndContinueNotificationAddress = useCallback(async () => {
    if (!pendingNotificationAddress) {
      closeUnsavedTogglesDialog();
      return;
    }

    const nextAddress = pendingNotificationAddress;
    closeUnsavedTogglesDialog();
    try {
      await saveNotificationToggles();
      setSelectedNotificationAddress(nextAddress);
    } catch {
      // Error shown by axios interceptor
    }
  }, [pendingNotificationAddress, closeUnsavedTogglesDialog, saveNotificationToggles]);

  const onDiscardNotificationAddressChanges = useCallback(() => {
    if (!pendingNotificationAddress) {
      closeUnsavedTogglesDialog();
      return;
    }

    if (notificationTypesData) {
      setNotificationToggles(
        mapNotificationCodesToToggles(notificationTypesData, notificationVisibility)
      );
    }
    setSelectedNotificationAddress(pendingNotificationAddress);
    closeUnsavedTogglesDialog();
  }, [
    pendingNotificationAddress,
    closeUnsavedTogglesDialog,
    notificationTypesData,
    notificationVisibility,
  ]);

  const onStartEditManageAddress = useCallback((id: string, currentAddress: string) => {
    setEditingId(id);
    setEditingDraft(currentAddress);
  }, []);

  const onSaveManageAddress = useCallback(
    async (id: string) => {
      const currentAddress =
        manageAddresses.find((addressItem) => addressItem.id === id)?.notificationAddress ?? '';
      const trimmedDraft = editingDraft.trim();
      if (!trimmedDraft || trimmedDraft === currentAddress.trim()) return;
      await updateNotificationEmailMutation.mutateAsync({
        contactMethodId: id,
        email: trimmedDraft,
      });
      toast.success(t('notificationEmailUpdatedSuccessfully'));
      setEditingId(null);
      setEditingDraft('');
    },
    [editingDraft, manageAddresses, updateNotificationEmailMutation, t]
  );

  const onCancelManageAddress = useCallback(() => {
    setEditingId(null);
    setEditingDraft('');
  }, []);

  const onDeleteManageAddress = useCallback(
    async (id: string) => {
      await deleteNotificationEmailMutation.mutateAsync(id);
      toast.success(t('notificationEmailDeletedSuccessfully'));
      setEditingId((current) => (current === id ? null : current));
      setEditingDraft('');
    },
    [deleteNotificationEmailMutation, t]
  );

  const onConfirmAddNotificationEmail = useCallback(
    async (email: string) => {
      const trimmed = email.trim();
      if (!trimmed) return;
      await addNotificationEmailMutation.mutateAsync({ email: trimmed });
      toast.success(t('notificationEmailAddedSuccessfully'));
      setSelectedNotificationAddress(trimmed);
    },
    [addNotificationEmailMutation, t]
  );

  useEffect(() => {
    setUserInfo((prev) => ({
      ...prev,
      companyName: user?.CompanyName ?? '',
      contactName: user?.ContactName ?? '',
    }));
  }, [user?.CompanyName, user?.ContactName]);

  useEffect(() => {
    if (notificationUserInfo?.email) {
      setDisplayEmail(notificationUserInfo.email);
      setUserInfo((prev) => ({ ...prev, ssoOrPasswordResetEmail: notificationUserInfo.email }));
    }
  }, [notificationUserInfo?.email]);

  useEffect(() => {
    if (notificationEmailsData) {
      const mapped = mapNotificationEmailsToManageAddresses(notificationEmailsData);
      setManageAddresses(mapped);
      const first = mapped[0];
      if (first) {
        setSelectedNotificationAddress(first.notificationAddress);
      }
    }
  }, [notificationEmailsData]);

  useEffect(() => {
    if (!selectedContactMethodId) {
      setNotificationToggles(initialToggles);
    } else if (notificationTypesData) {
      setNotificationToggles(
        mapNotificationCodesToToggles(notificationTypesData, notificationVisibility)
      );
    }
  }, [selectedContactMethodId, notificationTypesData, notificationVisibility]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasNotificationTogglesDirty) {
        e.preventDefault();
        return (e.returnValue = '');
      }
    };
    if (hasNotificationTogglesDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasNotificationTogglesDirty]);

  const value: ManageEmailsContextType = useMemo(
    () => ({
      userInfo,
      displayEmail,
      setDisplayEmail,
      updateClientEmail,
      isUpdatingClientEmail: updateClientEmailMutation.isPending,
      isNotificationUserInfoLoading,
      notificationUserInfoError: notificationUserInfoError as Error | null,
      selectedNotificationAddress,
      setSelectedNotificationAddress: setSelectedNotificationAddressWithConfirm,
      notificationToggles,
      notificationVisibility,
      isNotificationTypesLoading,
      setAllNotifications,
      setBuyerPurchaseOrders,
      setOcr,
      setBuyerInvoices,
      setMessages,
      updateNotificationToggles,
      saveNotificationToggles,
      hasNotificationTogglesDirty,
      isUpdatingNotificationTypes: updateNotificationTypesMutation.isPending,
      manageAddresses,
      setManageAddresses,
      isNotificationEmailsLoading,
      notificationEmailsError: notificationEmailsError as Error | null,
      editingId,
      setEditingId,
      editingDraft,
      setEditingDraft,
      onStartEditManageAddress,
      onSaveManageAddress,
      isUpdatingNotificationEmail: updateNotificationEmailMutation.isPending,
      onCancelManageAddress,
      onDeleteManageAddress,
      isDeletingNotificationEmail: deleteNotificationEmailMutation.isPending,
      onConfirmAddNotificationEmail,
      isAddingNotificationEmail: addNotificationEmailMutation.isPending,
      isAddingNewEmail,
      setIsAddingNewEmail,
      isUnsavedTogglesDialogOpen,
      closeUnsavedTogglesDialog,
      onSaveAndContinueNotificationAddress,
      onDiscardNotificationAddressChanges,
    }),
    [
      userInfo,
      displayEmail,
      updateClientEmail,
      updateClientEmailMutation.isPending,
      isNotificationUserInfoLoading,
      notificationUserInfoError,
      selectedNotificationAddress,
      notificationToggles,
      notificationVisibility,
      isNotificationTypesLoading,
      setAllNotifications,
      setBuyerPurchaseOrders,
      setOcr,
      setBuyerInvoices,
      setMessages,
      updateNotificationToggles,
      saveNotificationToggles,
      hasNotificationTogglesDirty,
      setSelectedNotificationAddressWithConfirm,
      updateNotificationTypesMutation.isPending,
      manageAddresses,
      isNotificationEmailsLoading,
      notificationEmailsError,
      editingId,
      editingDraft,
      onStartEditManageAddress,
      onSaveManageAddress,
      updateNotificationEmailMutation.isPending,
      onCancelManageAddress,
      onDeleteManageAddress,
      deleteNotificationEmailMutation.isPending,
      onConfirmAddNotificationEmail,
      addNotificationEmailMutation.isPending,
      isAddingNewEmail,
      isUnsavedTogglesDialogOpen,
      closeUnsavedTogglesDialog,
      onSaveAndContinueNotificationAddress,
      onDiscardNotificationAddressChanges,
    ]
  );

  return <ManageEmailsContext.Provider value={value}>{children}</ManageEmailsContext.Provider>;
};

export const useManageEmailsContext = () => {
  const context = useContext(ManageEmailsContext);
  if (!context) {
    throw new Error('useManageEmailsContext must be used within ManageEmailsContextProvider');
  }
  return context;
};
